import { convertBaseToDisplay } from '../common/conversions'
import { SensorProduct, UnitCategoryId } from '../common/enums'
import {
  ExportMetadata,
  CsvColumn,
  SensorReadings,
  ExportProgressCallback,
  ExportResult,
  TimestampFormat,
  ReadingValue,
} from './csvExportTypes'

// Product names that are rainfall sensors
const RAINFALL_PRODUCTS = new Set<string>([
  SensorProduct.Rainfall,
  SensorProduct.OpticalRainSensorSAB,
  SensorProduct.OpticalRainSensorPower,
])

// Product names that are flow meter sensors
const FLOW_METER_PRODUCTS = new Set<string>([
  SensorProduct.Badgermeter220BR,
  SensorProduct.DEMPaddleWheel,
  SensorProduct.BadgermeterSDI,
])

// Processing chunk size for large exports
const CHUNK_SIZE = 5000

/**
 * Format timestamp based on selected format
 */
function formatTimestamp(timestamp: number, format: TimestampFormat = 'iso'): string {
  const date = new Date(timestamp * 1000)  // Convert seconds to ms

  switch (format) {
    case 'unix':
      return timestamp.toString()

    case 'us': {
      // MM/DD/YYYY hh:mm:ss AM/PM
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      let hours = date.getHours()
      const ampm = hours >= 12 ? 'PM' : 'AM'
      hours = hours % 12 || 12
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`
    }

    case 'eu': {
      // DD/MM/YYYY HH:mm:ss
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
    }

    case 'iso':
    default: {
      // ISO 8601 with timezone offset
      const offset = -date.getTimezoneOffset()
      const sign = offset >= 0 ? '+' : '-'
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0')
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0')
      return date.toISOString().replace('Z', `${sign}${offsetHours}:${offsetMinutes}`)
    }
  }
}

/**
 * Format current date for filename
 */
function formatDateForFilename(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Generate default filename for export
 */
export function generateDefaultFilename(
  deviceName: string,
  from: number,
  to: number
): string {
  const fromDate = new Date(from)
  const toDate = new Date(to)

  // Format device name: RIC1030_0486 style (uppercase, underscore separator)
  const safeName = deviceName
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')

  return `${safeName}___${formatDateForFilename(fromDate)}_to_${formatDateForFilename(toDate)}`
}

/**
 * Get timezone string (e.g., "-07:00" or "+05:30")
 */
function getTimezoneOffsetString(): string {
  const offset = new Date().getTimezoneOffset()
  const sign = offset <= 0 ? '+' : '-'
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0')
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0')
  return `${sign}${hours}:${minutes}`
}

/**
 * Compute rainfall rate using 1-hour sliding window.
 * Input: sorted readings with values in meters (base unit).
 * Output: readings with rate values in mm/min (base unit for RainfallRate category).
 * Logic matches RainfallChart.tsx generateDataPoints().
 */
export function computeRainfallRate(
  readings: ReadingValue[]
): ReadingValue[] {
  const result: ReadingValue[] = []
  let window: { timestamp: number; value: number }[] = []

  for (const reading of readings) {
    if (reading.value === null) {
      result.push({ timestamp: reading.timestamp, value: null })
      continue
    }

    const ts = reading.timestamp
    const valueInMeters = reading.value

    // Add to sliding window and trim to 1 hour
    window.push({ timestamp: ts, value: valueInMeters })
    window = window.filter(r => r.timestamp > ts - 3600)

    // Sum values in window (meters), convert to mm/hour then to mm/min
    const sumMetersPerHour = window.reduce((acc, r) => acc + r.value, 0)
    const rateInMmPerHour = sumMetersPerHour * 1000
    const rateInMmPerMinute = rateInMmPerHour / 60

    result.push({ timestamp: ts, value: rateInMmPerMinute })
  }

  return result
}

/**
 * Compute cumulative sum of reading values.
 * Used for rainfall total and flow meter total volume.
 * Input values should be in base units; output is cumulative sum in same base units.
 */
export function computeCumulativeSum(
  readings: ReadingValue[]
): ReadingValue[] {
  const result: ReadingValue[] = []
  let total = 0

  for (const reading of readings) {
    if (reading.value !== null) {
      total += reading.value
    }
    result.push({ timestamp: reading.timestamp, value: reading.value !== null ? total : null })
  }

  return result
}

/**
 * Check if a product name is a rainfall sensor
 */
export function isRainfallProduct(productName: string): boolean {
  return RAINFALL_PRODUCTS.has(productName)
}

/**
 * Check if a product name is a flow meter sensor
 */
export function isFlowMeterProduct(productName: string): boolean {
  return FLOW_METER_PRODUCTS.has(productName)
}

/**
 * Additional stats for CSV metadata header
 */
export interface ExportStats {
  sensorCount: number
  totalReadings: number
  uniqueTimestamps: number
}

/**
 * Generate metadata header block for CSV
 */
export function generateMetadataHeader(
  metadata: ExportMetadata,
  timestampFormat: TimestampFormat = 'iso',
  stats?: ExportStats
): string {
  const lines = [
    '# ioAg Sensor Data Export',
    '#',
    `# Device: ${metadata.deviceName}`,
    `# Sigfox ID: ${metadata.sigfoxId}`,
    `# Site: ${metadata.siteName || 'N/A'}`,
  ]

  // Add extended device metadata
  if (metadata.serialNumber !== undefined) {
    lines.push(`# Serial Number: ${metadata.serialNumber}`)
  }
  if (metadata.wisolId) {
    lines.push(`# WISOL ID: ${metadata.wisolId}`)
  }
  if (metadata.hardwareVersion !== undefined) {
    lines.push(`# Hardware: ${metadata.hardwareVersion}`)
  }
  if (metadata.firmwareVersion !== undefined) {
    lines.push(`# Firmware: ${metadata.firmwareVersion}`)
  }

  // Coordinates
  lines.push('#')
  lines.push(`# Latitude: ${metadata.latitude ?? 'N/A'}`)
  lines.push(`# Longitude: ${metadata.longitude ?? 'N/A'}`)
  if (metadata.elevation !== undefined) {
    lines.push(`# Elevation: ${metadata.elevation} ft`)
  }

  lines.push('#')
  lines.push(`# Date Range: ${formatDateForFilename(metadata.dateRange.from)} to ${formatDateForFilename(metadata.dateRange.to)}`)
  lines.push(`# Exported At: ${metadata.exportedAt.toISOString()}`)

  // Add timezone info for formats that don't include it in timestamps
  if (timestampFormat === 'us' || timestampFormat === 'eu') {
    lines.push(`# Timezone: UTC${getTimezoneOffsetString()}`)
  }

  // Add export statistics
  lines.push('#')
  if (stats) {
    lines.push(`# Sensors Exported: ${stats.sensorCount}`)
    lines.push(`# Readings: ${stats.uniqueTimestamps}`)
    if (stats.uniqueTimestamps === 0) {
      lines.push('# Note: No data available for the selected date range')
    }
  }

  // Add flow meter sensor config metadata
  if (metadata.sensorMeta && Object.keys(metadata.sensorMeta).length > 0) {
    lines.push('#')
    for (const [port, meta] of Object.entries(metadata.sensorMeta)) {
      const label = meta.productName ? `${port} - ${meta.productName}` : port
      lines.push(`# Sensor Config (${label}):`)
      if (meta.kValue !== undefined) lines.push(`#   K-Value: ${meta.kValue}`)
      if (meta.diameter !== undefined) lines.push(`#   Diameter: ${meta.diameter}`)
      if (meta.area !== undefined) lines.push(`#   Area: ${meta.area} acres`)
    }
  }

  lines.push('#')
  return lines.join('\n')
}

/**
 * Generate column header based on sensor info
 * Format: Port_SensorName_Unit or Port_SensorName_SubType_Unit
 */
export function generateColumnHeader(
  port: string,
  sensorName: string,
  unitSymbol: string,
  subTypeLabel?: string
): string {
  // Use base port (strip sub-type key like "B1:rate" -> "B1")
  const basePort = port.includes(':') ? port.split(':')[0] : port

  // Sanitize sensor name (remove spaces, special chars)
  const safeName = sensorName
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')

  // Sanitize unit symbol
  const safeUnit = unitSymbol
    .replace(/[%°]/g, match => match === '%' ? '%' : '')
    .replace(/\s+/g, '')

  // Sanitize sub-type label
  const safeSubType = subTypeLabel
    ? '_' + subTypeLabel.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '')
    : ''

  return `${basePort}_${safeName}${safeSubType}_${safeUnit}`
}

/**
 * Build a unique timestamp index from all sensor readings
 * Returns sorted array of unique timestamps
 */
export function buildTimestampIndex(
  sensorReadings: SensorReadings[],
  excludeHidden: boolean = true
): number[] {
  const timestamps = new Set<number>()

  for (const sensor of sensorReadings) {
    for (const reading of sensor.data) {
      // Skip hidden readings if excludeHidden is true
      if (excludeHidden && sensor.hidden?.[reading.timestamp.toString()]) {
        continue
      }
      timestamps.add(reading.timestamp)
    }
  }

  // Sort chronologically
  return Array.from(timestamps).sort((a, b) => a - b)
}

/**
 * Process readings and apply unit conversion
 */
function processReadingValue(
  value: number | null,
  conversionFormula: string | undefined
): string {
  if (value === null || value === undefined) {
    return ''
  }

  // Apply unit conversion if formula provided
  const convertedValue = conversionFormula
    ? convertBaseToDisplay(value, conversionFormula)
    : value

  // Format number with reasonable precision
  return Number.isFinite(convertedValue)
    ? convertedValue.toFixed(2)
    : ''
}

/**
 * Yield to UI thread to prevent blocking during large exports
 */
async function yieldToUI(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0))
}

/**
 * Generate CSV content from sensor readings
 */
export async function generateCsvExport(
  metadata: ExportMetadata,
  sensorReadings: SensorReadings[],
  columns: CsvColumn[],
  unitConversions: Record<number, any>,
  onProgress?: ExportProgressCallback,
  timestampFormat: TimestampFormat = 'iso'
): Promise<string> {
  const csvParts: string[] = []

  // Phase 1: Preparing
  onProgress?.({
    phase: 'preparing',
    current: 0,
    total: 100,
    message: 'Building timestamp index...'
  })

  // Build timestamp index (excluding hidden readings)
  const timestamps = buildTimestampIndex(sensorReadings, true)
  const totalRows = timestamps.length

  // Calculate total data points across all sensors
  let totalDataPoints = 0
  for (const sensor of sensorReadings) {
    for (const reading of sensor.data) {
      // Only count non-hidden readings
      if (!sensor.hidden?.[reading.timestamp.toString()]) {
        totalDataPoints++
      }
    }
  }

  // Build export stats
  const stats: ExportStats = {
    sensorCount: sensorReadings.length,
    totalReadings: totalDataPoints,
    uniqueTimestamps: totalRows
  }

  // Generate metadata header with stats
  csvParts.push(generateMetadataHeader(metadata, timestampFormat, stats))

  // Generate column headers
  const headerRow = ['Timestamp', ...columns.map(c => c.header)]
  csvParts.push(headerRow.join(','))

  if (totalRows === 0) {
    return csvParts.join('\n')
  }

  // Phase 2: Processing - build lookup maps for each sensor
  onProgress?.({
    phase: 'processing',
    current: 0,
    total: totalRows,
    message: 'Processing sensor data...'
  })

  // Create lookup maps for fast timestamp->value access
  const sensorMaps: Map<number, number | null>[] = []

  for (const sensor of sensorReadings) {
    const valueMap = new Map<number, number | null>()
    for (const reading of sensor.data) {
      // Skip hidden readings
      if (sensor.hidden?.[reading.timestamp.toString()]) {
        continue
      }
      valueMap.set(reading.timestamp, reading.value)
    }
    sensorMaps.push(valueMap)
  }

  // Phase 3: Generate rows in chunks
  onProgress?.({
    phase: 'generating',
    current: 0,
    total: totalRows,
    message: 'Generating CSV rows...'
  })

  const dataRows: string[] = []

  for (let i = 0; i < totalRows; i += CHUNK_SIZE) {
    const chunkEnd = Math.min(i + CHUNK_SIZE, totalRows)

    for (let j = i; j < chunkEnd; j++) {
      const timestamp = timestamps[j]
      const row: string[] = [formatTimestamp(timestamp, timestampFormat)]

      // Add value for each sensor column
      for (let k = 0; k < sensorReadings.length; k++) {
        const sensor = sensorReadings[k]
        const valueMap = sensorMaps[k]
        const value = valueMap.get(timestamp) ?? null

        // Get conversion formula for this sensor's unit
        const unitCategory = unitConversions[sensor.unitCategoryId]
        const conversionFormula = unitCategory?.formulas?.[sensor.unitId]

        row.push(processReadingValue(value, conversionFormula))
      }

      dataRows.push(row.join(','))
    }

    // Update progress and yield to UI
    onProgress?.({
      phase: 'generating',
      current: chunkEnd,
      total: totalRows,
      message: `Generated ${chunkEnd.toLocaleString()} of ${totalRows.toLocaleString()} rows...`
    })

    await yieldToUI()
  }

  csvParts.push(...dataRows)

  // Phase 4: Complete
  onProgress?.({
    phase: 'complete',
    current: totalRows,
    total: totalRows,
    message: `Export complete: ${totalRows.toLocaleString()} rows`
  })

  return csvParts.join('\n')
}

/**
 * Extract readings from raw sensor data, handling different API formats.
 * Filters out hidden readings.
 */
function extractReadings(
  sensorData: any
): ReadingValue[] {
  let readings: Array<{ timestamp: number; value: any }> = []

  if (Array.isArray(sensorData.data)) {
    readings = sensorData.data
  } else if (sensorData.readings && typeof sensorData.readings === 'object') {
    readings = Object.entries(sensorData.readings).map(([ts, val]) => ({
      timestamp: Number(ts),
      value: val
    }))
  }

  const hidden = sensorData.hidden || {}
  return readings
    .filter(r => !hidden[r.timestamp.toString()])
    .map(r => ({
      timestamp: r.timestamp,
      value: typeof r.value === 'number' ? r.value : null
    }))
    .sort((a, b) => a.timestamp - b.timestamp)
}

/**
 * Convert raw API sensor data to SensorReadings format.
 * When expandSubTypes is true, rainfall and flow meter sensors are expanded
 * into 3 sub-type readings each (amount/rate/total or volume/volumeRate/totalVolume).
 */
export function transformSensorData(
  rawSensors: Record<string, any>,
  selectedPorts: Set<string>,
  unitOverrides?: Map<string, { unitId: number; unitSymbol: string }>,
  batteryData?: any,
  expandSubTypes: boolean = false
): SensorReadings[] {
  const result: SensorReadings[] = []
  const processedFlowPorts = new Set<string>()

  for (const [sensorKey, sensorData] of Object.entries(rawSensors)) {
    const [port] = sensorKey.split('-')
    const productName = sensorData.metadata?.productName ?? sensorData.productName ?? 'Unknown'
    const unitCategoryId = sensorData.metadata?.units?.category?.id ?? 0

    if (expandSubTypes && isRainfallProduct(productName)) {
      // Rainfall: expand single reading into 3 sub-types
      const amountKey = `${port}:amount`
      const rateKey = `${port}:rate`
      const totalKey = `${port}:total`

      // Check if any sub-type for this port is selected
      const anySelected = selectedPorts.has(amountKey) || selectedPorts.has(rateKey) || selectedPorts.has(totalKey)
      if (!anySelected) continue

      const baseReadings = extractReadings(sensorData)

      if (selectedPorts.has(amountKey)) {
        const override = unitOverrides?.get(amountKey)
        result.push({
          port: amountKey,
          unitCategoryId: UnitCategoryId.Rainfall,
          unitId: override?.unitId ?? sensorData.metadata?.units?.id ?? 0,
          unitSymbol: override?.unitSymbol ?? sensorData.metadata?.units?.symbol ?? '',
          productName,
          data: baseReadings,
          hidden: undefined, // already filtered
        })
      }

      if (selectedPorts.has(rateKey)) {
        const override = unitOverrides?.get(rateKey)
        result.push({
          port: rateKey,
          unitCategoryId: UnitCategoryId.RainfallRate,
          unitId: override?.unitId ?? 0,
          unitSymbol: override?.unitSymbol ?? 'mm/min',
          productName,
          data: computeRainfallRate(baseReadings),
          hidden: undefined,
        })
      }

      if (selectedPorts.has(totalKey)) {
        const override = unitOverrides?.get(totalKey)
        result.push({
          port: totalKey,
          unitCategoryId: UnitCategoryId.Rainfall,
          unitId: override?.unitId ?? sensorData.metadata?.units?.id ?? 0,
          unitSymbol: override?.unitSymbol ?? sensorData.metadata?.units?.symbol ?? '',
          productName,
          data: computeCumulativeSum(baseReadings),
          hidden: undefined,
        })
      }

      continue
    }

    if (expandSubTypes && isFlowMeterProduct(productName)) {
      // Flow meter: deduplicate - API returns 2 keys per port (Volume + VolumeRate)
      if (processedFlowPorts.has(port)) continue
      processedFlowPorts.add(port)

      const volumeKey = `${port}:volume`
      const volumeRateKey = `${port}:volumeRate`
      const totalVolumeKey = `${port}:totalVolume`

      const anySelected = selectedPorts.has(volumeKey) || selectedPorts.has(volumeRateKey) || selectedPorts.has(totalVolumeKey)
      if (!anySelected) continue

      // Find both API entries for this port
      const portEntries = Object.entries(rawSensors).filter(([key]) => key.startsWith(port + '-'))
      const volumeEntry = portEntries.find(([, data]) => {
        const catName = data.metadata?.units?.category?.longName
        const catId = data.metadata?.units?.category?.id
        return catName === 'Volume' || catId === UnitCategoryId.Volume
      })
      const rateEntry = portEntries.find(([, data]) => {
        const catName = data.metadata?.units?.category?.longName
        const catId = data.metadata?.units?.category?.id
        return catName === 'Volume Rate' || catId === UnitCategoryId.VolumeRate
      })

      if (selectedPorts.has(volumeKey) && volumeEntry) {
        const override = unitOverrides?.get(volumeKey)
        const volData = volumeEntry[1]
        result.push({
          port: volumeKey,
          unitCategoryId: UnitCategoryId.Volume,
          unitId: override?.unitId ?? volData.metadata?.units?.id ?? 0,
          unitSymbol: override?.unitSymbol ?? volData.metadata?.units?.symbol ?? '',
          productName,
          data: extractReadings(volData),
          hidden: undefined,
        })
      }

      if (selectedPorts.has(volumeRateKey) && rateEntry) {
        const override = unitOverrides?.get(volumeRateKey)
        const rateData = rateEntry[1]
        result.push({
          port: volumeRateKey,
          unitCategoryId: UnitCategoryId.VolumeRate,
          unitId: override?.unitId ?? rateData.metadata?.units?.id ?? 0,
          unitSymbol: override?.unitSymbol ?? rateData.metadata?.units?.symbol ?? '',
          productName,
          data: extractReadings(rateData),
          hidden: undefined,
        })
      }

      if (selectedPorts.has(totalVolumeKey) && volumeEntry) {
        const override = unitOverrides?.get(totalVolumeKey)
        const volData = volumeEntry[1]
        result.push({
          port: totalVolumeKey,
          unitCategoryId: UnitCategoryId.TotalVolume,
          unitId: override?.unitId ?? 0,
          unitSymbol: override?.unitSymbol ?? '',
          productName,
          data: computeCumulativeSum(extractReadings(volData)),
          hidden: undefined,
        })
      }

      continue
    }

    // Standard sensor (non-expandable or expandSubTypes is false)
    if (!selectedPorts.has(port)) continue

    const override = unitOverrides?.get(port)
    const unitId = override?.unitId ?? sensorData.metadata?.units?.id ?? 0
    const unitSymbol = override?.unitSymbol ?? sensorData.metadata?.units?.symbol ?? ''

    // Get readings data - handle different API formats
    let readings: Array<{ timestamp: number; value: any }> = []
    if (Array.isArray(sensorData.data)) {
      readings = sensorData.data
    } else if (sensorData.readings && typeof sensorData.readings === 'object') {
      readings = Object.entries(sensorData.readings).map(([ts, val]) => ({
        timestamp: Number(ts),
        value: val
      }))
    }

    result.push({
      port,
      unitCategoryId,
      unitId,
      unitSymbol,
      productName,
      data: readings.map(r => ({
        timestamp: r.timestamp,
        value: typeof r.value === 'number' ? r.value : null
      })),
      hidden: sensorData.hidden
    })
  }

  // Add battery data if selected
  if (selectedPorts.has('BAT') && batteryData?.data?.battery) {
    const batteryReadings = batteryData.data.battery
    result.push({
      port: 'BAT',
      unitCategoryId: 0,
      unitId: 0,
      unitSymbol: 'V',
      productName: 'Battery Voltage',
      data: batteryReadings.map((r: any) => ({
        timestamp: r.timestamp,
        value: typeof r.value === 'number' ? r.value : null
      })),
      hidden: undefined
    })
  }

  // Sort by port for consistent column order (BAT last)
  return result.sort((a, b) => {
    if (a.port === 'BAT') return 1
    if (b.port === 'BAT') return -1
    if (a.port.startsWith('T0')) return 1
    if (b.port.startsWith('T0')) return -1
    return a.port.localeCompare(b.port)
  })
}

/**
 * Download CSV file to user's device
 */
export function downloadCsvFile(
  csvContent: string,
  filename: string
): ExportResult {
  try {
    // Add UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8'
    })

    // Create download link
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.csv`

    // Trigger download
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    // Count rows (subtract header lines)
    const rowCount = csvContent.split('\n').filter(line =>
      !line.startsWith('#') && line.trim() !== ''
    ).length - 1  // Subtract header row

    return {
      success: true,
      filename: `${filename}.csv`,
      rowCount: Math.max(0, rowCount)
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to download file'
    }
  }
}

/**
 * Estimate reading count for selected sensors and date range
 */
export function estimateReadingCount(
  sensorReadings: SensorReadings[],
  from: number,
  to: number
): number {
  let count = 0

  for (const sensor of sensorReadings) {
    for (const reading of sensor.data) {
      if (reading.timestamp >= from && reading.timestamp <= to) {
        // Exclude hidden readings
        if (!sensor.hidden?.[reading.timestamp.toString()]) {
          count++
        }
      }
    }
  }

  return count
}

/**
 * Check if any selected sensors have data in the given range
 */
export function hasDataInRange(
  sensorReadings: SensorReadings[],
  from: number,
  to: number
): boolean {
  for (const sensor of sensorReadings) {
    for (const reading of sensor.data) {
      if (reading.timestamp >= from && reading.timestamp <= to) {
        if (!sensor.hidden?.[reading.timestamp.toString()]) {
          return true
        }
      }
    }
  }
  return false
}
